import { type ElementRef, type FC, useCallback, useRef } from 'react';

import {
  Button,
  Icon,
  NativeVideoPlayer,
  preventForwardProps,
  styled,
  Typography,
  useIsHovered,
  useIsMounted,
  useIsVideoPlaying,
  VisuallyHidden,
} from '@cofenster/web-components';

import type { SharingLink } from '../../server/sharingLink/repository/SharingLinkRepository';
import { Captions } from '../components/Captions';
import { useTrackWatchTime } from '../hooks/useTrackWatchTime';
import { TrackView } from './TrackView';

const EmbeddedMain = styled('main')({
  height: '100vh',
  overflow: 'hidden',
});

const OverlayContainer = styled('div')(() => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  left: 0,
  bottom: 0,
  pointerEvents: 'none',
}));

const StyledVideo = styled(NativeVideoPlayer)(({ theme }) => ({
  backgroundColor: theme.palette.brand.carbon,
}));

const Title = styled(
  Typography,
  preventForwardProps(['alwaysShow'])
)<{ alwaysShow: boolean }>(({ theme, alwaysShow }) => ({
  position: 'absolute',
  margin: theme.spacing(2),
  transition: 'opacity 200ms ease',
  opacity: alwaysShow ? 1 : 0,
  'main:hover &, main:focus-within &': {
    opacity: 1,
  },
}));

const PlayButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  left: `calc(50% - ${theme.spacing(5)})`,
  top: `calc(50% - ${theme.spacing(5)})`,
  width: theme.spacing(10),
  height: theme.spacing(10),
  borderRadius: '50%',
  pointerEvents: 'initial',
}));

export const EmbeddedVideoPage: FC<{ sharingLink: SharingLink; defaultCaptionLanguage?: string }> = ({
  sharingLink,
  defaultCaptionLanguage,
}) => {
  const videoRef = useRef<ElementRef<'video'>>(null);
  const isMounted = useIsMounted();

  const isPlaying = useIsVideoPlaying(videoRef.current);
  const isHovered = useIsHovered(videoRef.current);

  const onPlay = useCallback(() => {
    videoRef?.current?.play();
  }, []);

  useTrackWatchTime(sharingLink.projectId, videoRef.current);

  return (
    <EmbeddedMain>
      <StyledVideo
        src={sharingLink.videoUrl}
        ref={videoRef}
        controls={isHovered}
        objectFit="contain"
        actions={['FULLSCREEN', 'PICTURE_IN_PICTURE', 'PLAYBACK_RATE']}
      >
        <Captions sharingLink={sharingLink} defaultLanguage={defaultCaptionLanguage} />
      </StyledVideo>
      <TrackView video={videoRef.current} videoId={sharingLink.projectId} />
      <OverlayContainer>
        <Title color="white" variant="h4" component="h1" alwaysShow={!isPlaying}>
          {sharingLink.name}
        </Title>
        {!isPlaying && isMounted && (
          <PlayButton onClick={onPlay} variant="blurred" data-testid="play-button">
            <Icon type="PlayIcon" />
            <VisuallyHidden>i18n.global.play</VisuallyHidden>
          </PlayButton>
        )}
      </OverlayContainer>
    </EmbeddedMain>
  );
};
// displayName is required by `ssrMiddleware::generateRenderWithHydration` function
EmbeddedVideoPage.displayName = 'EmbeddedVideoPage';
